import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GraduationCap, Users } from "lucide-react";

export default function Login() {
  const { role } = useParams(); // "ogretmen" or "aile"
  const isTeacher = role === "ogretmen";
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { phone, password });
      setUser(data.user);
      toast.success("Hoş geldin!");
      // route based on completion
      if (data.user.role === "admin") {
        nav("/admin-dashboard");
      } else if (!data.user.profile_complete) {
        nav(data.user.role === "teacher" ? "/ogretmen/kurulum" : "/aile/kurulum");
      } else {
        nav(data.user.role === "teacher" ? "/ogretmen/profil" : "/ara");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="flex justify-center mb-8 reveal">
          <div className="h-14 w-14 rounded-full bg-[color:var(--brand)] text-white grid place-items-center shadow-lg shadow-blue-500/30">
            {isTeacher ? <GraduationCap size={22} /> : <Users size={22} />}
          </div>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-center tracking-tight reveal" data-testid="login-title">
          {isTeacher ? "Öğretmen Girişi" : "Aile Girişi"}
        </h1>
        <p className="mt-3 text-center text-neutral-500 reveal reveal-delay-1">Telefon numaran ve şifrenle giriş yap.</p>

        <form onSubmit={submit} className="mt-10 space-y-5 reveal reveal-delay-2">
          <div>
            <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Telefon numarası</Label>
            <Input
              id="phone"
              type="tel"
              data-testid="login-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05XX XXX XX XX"
              required
              className="mt-2 h-12 rounded-lg border-neutral-300 focus:border-neutral-900"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Şifre</Label>
            <Input
              id="password"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 h-12 rounded-lg border-neutral-300 focus:border-neutral-900"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-12 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white text-base shadow-md shadow-blue-500/20"
          >
            {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </Button>
        </form>

        <p className="mt-8 text-center text-sm text-neutral-600">
          Hesabın yok mu?{" "}
          <Link to="/kayit" className="font-medium text-neutral-900 underline underline-offset-4" data-testid="signup-link">
            Kayıt Ol
          </Link>
        </p>

        <div className="mt-10 text-center">
          <Link
            to={isTeacher ? "/giris/aile" : "/giris/ogretmen"}
            className="text-xs uppercase tracking-widest text-neutral-500 hover:text-neutral-900"
            data-testid="switch-role-link"
          >
            {isTeacher ? "Aile olarak giriş yapmak istiyorum" : "Öğretmen olarak giriş yapmak istiyorum"}
          </Link>
        </div>
      </div>
    </Layout>
  );
}
